import { useEffect, useState } from 'react';
import {
  GrcButton,
  GrcButtonStylingMode,
  GrcButtonType,
  GrcPopup,
  GrcTextBox
} from '@grc/ui-package';
import TenantDetailsFields from './tenant-details-fields';
import IdentityAccordion, { toIdentityPatterns, useIdentityPatterns } from './identity-accordion';
import {
  MIN_OWNER_PASSWORD_LENGTH,
  TenantCommonDetails,
  emptyCommonDetails,
  isEmailAddress,
  isFilled,
  isOptionalHttpsUrl,
  useFieldState
} from './tenant-form';
import { CreateTenantRequest, IdentitySeeds } from './tenant-types';

type CreateState = TenantCommonDetails & {
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
  ownerPassword: string;
};

const emptyCreateState: CreateState = {
  ...emptyCommonDetails,
  ownerFirstName: '',
  ownerLastName: '',
  ownerEmail: '',
  ownerPassword: ''
};

interface TenantCreateDialogProps {
  open: boolean;
  onCancel: () => void;
  onCreate: (request: CreateTenantRequest) => Promise<void>;
}

/**
 * Create is the only flow that sets identity patterns and owner credentials, so it
 * is the only one that needs the room a modal gives. Editing an existing tenant is
 * a different shape entirely and lives in tenant-edit-drawer.
 */
export default function TenantCreateDialog({ open, onCancel, onCreate }: TenantCreateDialogProps) {
  const { values, touched, setField, markTouched, reset } = useFieldState(emptyCreateState);
  const identities = useIdentityPatterns();
  const [submitting, setSubmitting] = useState(false);

  const resetIdentities = identities.reset;
  useEffect(() => {
    if (!open) return;
    reset(emptyCreateState);
    resetIdentities();
  }, [open, reset, resetIdentities]);

  const nameValid = isFilled(values.name);
  const carrierPortalUrlValid = isOptionalHttpsUrl(values.carrierPortalUrl);
  const customerPortalUrlValid = isOptionalHttpsUrl(values.customerPortalUrl);
  const ownerFirstNameValid = isFilled(values.ownerFirstName);
  const ownerLastNameValid = isFilled(values.ownerLastName);
  const ownerEmailValid = isEmailAddress(values.ownerEmail);
  const ownerPasswordValid = values.ownerPassword.length >= MIN_OWNER_PASSWORD_LENGTH;

  // Each check also drives isValid on its own field; the disabled Save is the backstop.
  const canSubmit =
    nameValid
    && carrierPortalUrlValid
    && customerPortalUrlValid
    && ownerFirstNameValid
    && ownerLastNameValid
    && ownerEmailValid
    && ownerPasswordValid
    && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const identitySeeds: IdentitySeeds = { ...identities.seeds };
      await onCreate({
        ...values,
        identitySeeds,
        identityPatterns: toIdentityPatterns(identities.templates)
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <GrcPopup
      visible={open}
      onHiding={onCancel}
      dragEnabled={false}
      hideOnOutsideClick={false}
      showCloseButton={true}
      showTitle={true}
      title="Create Tenant"
      width={820}
      height={720}
    >
      <div className="tenant-create-dialog">
        <div className="tenant-create-scroll">
          <div className="tenant-form">
            <TenantDetailsFields
              values={values}
              setField={setField}
              markTouched={markTouched}
              nameInvalid={!!touched.name && !nameValid}
            carrierPortalUrlInvalid={!!touched.carrierPortalUrl && !carrierPortalUrlValid}
            customerPortalUrlInvalid={!!touched.customerPortalUrl && !customerPortalUrlValid}
            />
          </div>

          <h3 className="form-section-title">Owner</h3>
          <div className="tenant-form">
            <GrcTextBox
              id="tenant-owner-first-name"
              label="Owner First Name"
              labelMode="static"
              stylingMode="outlined"
              value={values.ownerFirstName}
              onValueChanged={setField('ownerFirstName')}
              onFocusOut={markTouched('ownerFirstName')}
              isValid={touched.ownerFirstName ? ownerFirstNameValid : true}
              validationError={{ message: 'Owner first name is required.' }}
              validationMessageMode="auto"
            />
            <GrcTextBox
              id="tenant-owner-last-name"
              label="Owner Last Name"
              labelMode="static"
              stylingMode="outlined"
              value={values.ownerLastName}
              onValueChanged={setField('ownerLastName')}
              onFocusOut={markTouched('ownerLastName')}
              isValid={touched.ownerLastName ? ownerLastNameValid : true}
              validationError={{ message: 'Owner last name is required.' }}
              validationMessageMode="auto"
            />
            <GrcTextBox
              id="tenant-owner-email"
              label="Owner Email"
              labelMode="static"
              stylingMode="outlined"
              mode="email"
              className="full-width"
              value={values.ownerEmail}
              onValueChanged={setField('ownerEmail')}
              onFocusOut={markTouched('ownerEmail')}
              isValid={touched.ownerEmail ? ownerEmailValid : true}
              validationError={{ message: 'Enter a valid email address.' }}
              validationMessageMode="auto"
            />
            <GrcTextBox
              id="tenant-owner-password"
              label="Owner Password"
              labelMode="static"
              stylingMode="outlined"
              mode="password"
              className="full-width"
              value={values.ownerPassword}
              onValueChanged={setField('ownerPassword')}
              onFocusOut={markTouched('ownerPassword')}
              isValid={touched.ownerPassword ? ownerPasswordValid : true}
              validationError={{ message: `Use at least ${MIN_OWNER_PASSWORD_LENGTH} characters.` }}
              validationMessageMode="auto"
            />
          </div>

          <h3 className="form-section-title">Identities</h3>
          <p className="identities-help">
            Each identity is a Template the backend parses directly. Supported placeholders: {'{ID}'},{' '}
            {'{RANDOM[N]}'}, {'{PICKUPDATE:fmt}'}, {'{INVOICEDATE:fmt}'}, {'{CREATEDATE:fmt}'},{' '}
            {'{QUOTEDATE:fmt}'}, {'{LOADBOARDDATE:fmt}'}. Anything else is treated as literal text. The
            Seed is the starting integer for {'{ID}'} — generators begin at seed + 1. Templates are fixed
            once the tenant exists.
          </p>
          <IdentityAccordion state={identities} />
        </div>

        <div className="form-actions">
          <GrcButton
            id="tenant-cancel-button"
            text="Cancel"
            type={GrcButtonType.Normal}
            stylingMode={GrcButtonStylingMode.Outlined}
            onClick={onCancel}
          />
          <GrcButton
            id="tenant-save-button"
            text="Save"
            type={GrcButtonType.Default}
            stylingMode={GrcButtonStylingMode.Contained}
            disabled={!canSubmit}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </GrcPopup>
  );
}
